import { t, useLanguage } from '../../i18n';
import React, { useState, useEffect, useContext } from 'react';
import { ThemePreferencesContext } from '../../contexts/ThemeContext';
import { View, StyleSheet, TouchableOpacity, Alert, FlatList } from 'react-native';
import { Text, Card, Button, useTheme, Chip, ActivityIndicator, Menu, Divider } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { getDisplayColor } from '../../utils/courseColors';
import { Course } from '../../types';
import { useSchedule } from '../../contexts/ScheduleContext';

type AttendanceScreenProps = {
  navigation: any;
};

const AttendanceScreen = ({ navigation }: AttendanceScreenProps) => {
  useLanguage();
  const { toggleTheme, isDarkMode } = useContext(ThemePreferencesContext);
  const { userCourses, updateCourse, isLoading: scheduleLoading } = useSchedule();
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const [initialCourses, setInitialCourses] = useState<Course[]>([]);
  const [displayedCourses, setDisplayedCourses] = useState<Course[]>([]);
  const [selectedDay, setSelectedDay] = useState<number | undefined>(undefined);
  const [selectedPeriod, setSelectedPeriod] = useState<number | undefined>(undefined);
  const [dayMenuVisible, setDayMenuVisible] = useState(false);
  const [periodMenuVisible, setPeriodMenuVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [categoryMenuVisible, setCategoryMenuVisible] = useState(false);
  const [selectedSemester, setSelectedSemester] = useState<string | undefined>(undefined);
  const [semesterMenuVisible, setSemesterMenuVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sortMenuVisible, setSortMenuVisible] = useState(false);
  const [sortOption, setSortOption] = useState<'name'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const theme = useTheme();

  const daysOfWeek = [
    { label: t('すべての曜日'), value: undefined },
    ...['月曜', '火曜', '水曜', '木曜', '金曜', '土曜', '日曜'].map((day, index) => ({ label: t(day), value: index + 1 })),
  ];

  const periods = [
    { label: t('すべての時限'), value: undefined },
    ...Array.from({ length: 6 }, (_, i) => ({ label: t('{0}限', [i + 1]), value: i + 1 })),
  ];

  const semesters = [
    { label: t('すべての学期'), value: undefined },
    { label: t('春学期'), value: '春学期' },
    { label: t('秋学期'), value: '秋学期' },
  ];

  const categories = [
    { label: t("すべてのカテゴリ"), value: undefined },
    ...Array.from(new Set(userCourses.map(course => course.category).filter(Boolean))).map(category => ({ label: t(category), value: category })),
  ];

  useEffect(() => {
    fetchCourses();
  }, [userCourses, scheduleLoading]);

  useEffect(() => {
    let filtered = [...initialCourses];

    if (selectedDay !== undefined) {
      filtered = filtered.filter(course => course.dayOfWeek === selectedDay);
    }

    if (selectedPeriod !== undefined) {
      filtered = filtered.filter(course => course.period === selectedPeriod);
    }

    if (selectedSemester !== undefined) {
      filtered = filtered.filter(course => course.semester === selectedSemester);
    }

    if (selectedCategory !== undefined) {
      filtered = filtered.filter(course => course.category === selectedCategory);
    }

    const sorted = [...filtered].sort((a, b) => {
      if (sortOption === 'name') {
        return sortDirection === 'asc'
          ? (a.name || '').localeCompare(b.name || '')
          : (b.name || '').localeCompare(a.name || '');
      }
      return 0;
    });
    setDisplayedCourses(sorted);

  }, [initialCourses, selectedDay, selectedPeriod, selectedSemester, selectedCategory, sortOption, sortDirection]);


  const fetchCourses = async () => {
    setInitialCourses(userCourses.filter(course => !course.isCustom));
    setLoading(scheduleLoading);
  };

  const updateAttendance = async (courseId: string, status: 'attended' | 'absent' | 'late') => {
    if (updatingId) return;
    const course = userCourses.find(item => item.id === courseId);
    if (!course) return;
    const field = { attended: 'attendedClasses', absent: 'absentClasses', late: 'lateClasses' }[status];
    setUpdatingId(courseId);
    try {
      await updateCourse(courseId, { [field]: Math.max(0, Number((course as any)[field]) || 0) + 1 });
    } catch {
      Alert.alert(t('エラー'), t('操作に失敗しました'));
    } finally { setUpdatingId(null); }
  };

  const getDayOfWeekText = (dayOfWeek: number) => {
    const days = ['', '月', '火', '水', '木', '金', '土', '日'];
    return t(days[dayOfWeek] || '');
  };

  const renderCourseItem = ({ item }: { item: Course }) => {
    const cardColor = getDisplayColor(item) || theme.colors.surface;
    const textColor = isDarkMode ? '#FFFFFF' : theme.colors.onSurface;

    const attended = item.attendedClasses ?? 0;
    const absents = item.absentClasses ?? 0;
    const lates = item.lateClasses ?? 0;
    const total = attended + absents + lates;

    return (
      <Card style={[styles.courseCard, { backgroundColor: cardColor }]}>
        <Card.Content>
          <View style={styles.courseHeader}>
            <Text style={[styles.courseName, { color: textColor }]}>{item.name}</Text>
            <Chip
              icon="information-outline"
              style={{ backgroundColor: 'rgba(0,0,0,0.1)', height: 30 }}
              textStyle={{ color: textColor, fontSize: 12 }}
            >
              {t('{0}曜{1}限', [getDayOfWeekText(item.dayOfWeek as number), item.period])}
            </Chip>
          </View>
          <Text style={[styles.courseInfo, { color: textColor, opacity: 0.9 }]}>
            {item.professor} {t("教室：")}{item.room}
          </Text>

          <View style={styles.attendanceContainer}>
            <View style={[styles.statsContainer, { marginTop: 8 }]}>
              <View style={styles.statItem}>
                <Text style={[styles.statNumber, { color: textColor }]}>{attended}</Text>
                <Text style={[styles.statLabel, { color: textColor, opacity: 0.8 }]}>{t("出席")}</Text>
              </View>
              <View style={styles.statItem}>
                <Text style={[styles.statNumber, { color: textColor }]}>{absents}</Text>
                <Text style={[styles.statLabel, { color: textColor, opacity: 0.8 }]}>{t("欠席")}</Text>
              </View>
              <View style={styles.statItem}>
                <Text style={[styles.statNumber, { color: textColor }]}>{lates}</Text>
                <Text style={[styles.statLabel, { color: textColor, opacity: 0.8 }]}>{t("遅刻")}</Text>
              </View>
              <View style={styles.statItem}>
                <Text style={[styles.statNumber, { color: textColor }]}>{total}</Text>
                <Text style={[styles.statLabel, { color: textColor, opacity: 0.8 }]}>{t("合計")}</Text>
              </View>
            </View>

            <View style={styles.buttonContainer}>
              <Button
                mode="contained-tonal"
                onPress={() => updateAttendance(item.id, 'attended')}
                disabled={updatingId !== null}
                style={styles.attendanceButton}
                labelStyle={{ fontSize: 12 }}
                icon="check-circle-outline"
                buttonColor="rgba(255,255,255,0.2)"
                textColor={textColor}
              >
                {t("出席")}</Button>
              <Button
                mode="contained-tonal"
                onPress={() => updateAttendance(item.id, 'absent')}
                disabled={updatingId !== null}
                style={styles.attendanceButton}
                labelStyle={{ fontSize: 12 }}
                icon="close-circle-outline"
                buttonColor="rgba(255,255,255,0.2)"
                textColor={textColor}
              >
                {t("欠席")}</Button>
              <Button
                mode="contained-tonal"
                onPress={() => updateAttendance(item.id, 'late')}
                disabled={updatingId !== null}
                style={styles.attendanceButton}
                labelStyle={{ fontSize: 12 }}
                icon="clock-alert-outline"
                buttonColor="rgba(255,255,255,0.2)"
                textColor={textColor}
              >
                {t("遅刻")}</Button>
            </View>
          </View>

        </Card.Content>
      </Card>
    );
  };

  const renderEmptyList = () => (
    <View style={styles.emptyContainer}>
      <Ionicons
        name="school-outline"
        size={64}
        color={theme.colors.onBackground}
        style={{ opacity: 0.5 }}
      />
      <Text style={[styles.emptyText, { color: theme.colors.onBackground }]}>
        {t("登録されている授業はありません")}</Text>
      <Text style={[styles.emptySubtext, { color: theme.colors.onBackground, opacity: 0.7 }]}>
        {t("「履修科目検索」から授業を追加してください")}</Text>
    </View>
  );

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.header}>
        <Text style={[styles.title, { color: theme.colors.primary }]}>{t('出席管理')}</Text>
        <View style={styles.headerActions}>
          <Menu
            visible={dayMenuVisible}
            onDismiss={() => setDayMenuVisible(false)}
            anchor={
              <TouchableOpacity
                style={styles.filterButton}
                onPress={() => setDayMenuVisible(true)}
              >
                <Text style={{ color: theme.colors.primary }}>
                  {daysOfWeek.find(d => d.value === selectedDay)?.label || t('すべての曜日')}
                </Text>
              </TouchableOpacity>
            }
          >
            {daysOfWeek.map((day) => (
              <Menu.Item
                key={day.label}
                onPress={() => {
                  setSelectedDay(day.value);
                  setDayMenuVisible(false);
                }}
                title={t(day.label)}
                trailingIcon={selectedDay === day.value ? 'check' : undefined}
              />
            ))}
          </Menu>

          <Menu
            visible={periodMenuVisible}
            onDismiss={() => setPeriodMenuVisible(false)}
            anchor={
              <TouchableOpacity
                style={styles.filterButton}
                onPress={() => setPeriodMenuVisible(true)}
              >
                <Text style={{ color: theme.colors.primary }}>
                  {periods.find(p => p.value === selectedPeriod)?.label || t('すべての時限')}
                </Text>
              </TouchableOpacity>
            }
          >
            {periods.map((p) => (
              <Menu.Item
                key={p.label}
                onPress={() => {
                  setSelectedPeriod(p.value);
                  setPeriodMenuVisible(false);
                }}
                title={t(p.label)}
                trailingIcon={selectedPeriod === p.value ? 'check' : undefined}
              />
            ))}
          </Menu>

          <Menu
            visible={semesterMenuVisible}
            onDismiss={() => setSemesterMenuVisible(false)}
            anchor={
              <TouchableOpacity
                style={styles.filterButton}
                onPress={() => setSemesterMenuVisible(true)}
              >
                <Text style={{ color: theme.colors.primary }}>
                  {semesters.find(s => s.value === selectedSemester)?.label || t('すべての学期')}
                </Text>
              </TouchableOpacity>
            }
          >
            {semesters.map((s) => (
              <Menu.Item
                key={s.label}
                onPress={() => {
                  setSelectedSemester(s.value);
                  setSemesterMenuVisible(false);
                }}
                title={t(s.label)}
                trailingIcon={selectedSemester === s.value ? 'check' : undefined}
              />
            ))}
          </Menu>

          <Menu
            visible={categoryMenuVisible}
            onDismiss={() => setCategoryMenuVisible(false)}
            anchor={
              <TouchableOpacity
                style={styles.filterButton}
                onPress={() => setCategoryMenuVisible(true)}
              >
                <Text style={{ color: theme.colors.primary }}>
                  {categories.find(c => c.value === selectedCategory)?.label || t("すべてのカテゴリ")}
                </Text>
              </TouchableOpacity>
            }
          >
            {categories.map((c) => (
              <Menu.Item
                key={c.label}
                onPress={() => {
                  setSelectedCategory(c.value);
                  setCategoryMenuVisible(false);
                }}
                title={t(c.label)}
                trailingIcon={selectedCategory === c.value ? 'check' : undefined}
              />
            ))}
          </Menu>

          <Menu
            visible={sortMenuVisible}
            onDismiss={() => setSortMenuVisible(false)}
            anchor={
              <TouchableOpacity
                style={styles.sortButton}
                onPress={() => setSortMenuVisible(true)}
              >
                <Ionicons name="funnel-outline" size={24} color={theme.colors.primary} />
              </TouchableOpacity>
            }
          >
            <Menu.Item
              onPress={() => {
                setSortOption('name');
                setSortMenuVisible(false);
              }}
              title={t('授業名で並べ替え')}
              leadingIcon="sort-alphabetical-ascending"
              trailingIcon={sortOption === 'name' ? 'check' : undefined}
            />
            <Divider />
            <Menu.Item
              onPress={() => {
                setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                setSortMenuVisible(false);
              }}
              title={t('順序: {0}', [t(sortDirection === 'asc' ? '昇順' : '降順')])}
              leadingIcon={sortDirection === 'asc' ? 'sort-ascending' : 'sort-descending'}
            />
          </Menu>

          <TouchableOpacity onPress={toggleTheme} style={{ marginLeft: 8 }}>
            <Ionicons
              name={isDarkMode ? 'sunny-outline' : 'moon-outline'}
              size={24}
              color={theme.colors.primary}
            />
          </TouchableOpacity>
        </View>
      </View>

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={[styles.loadingText, { color: theme.colors.onBackground }]}>
            {t("授業を読み込み中...")}</Text>
        </View>
      ) : (
        <FlatList
          data={displayedCourses}
          renderItem={renderCourseItem}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContainer}
          ListEmptyComponent={renderEmptyList}
          refreshing={loading}
          onRefresh={fetchCourses}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  filterButton: {
    marginRight: 12,
    paddingVertical: 6,
    paddingHorizontal: 10,
  },
  sortButton: {
    marginRight: 16,
  },
  menu: {
    width: 250,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
  },
  listContainer: {
    padding: 16,
    paddingBottom: 24,
  },
  courseCard: {
    marginBottom: 16,
    elevation: 2,
  },
  courseHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  courseName: {
    fontSize: 18,
    fontWeight: 'bold',
    flex: 1,
    marginRight: 8,
  },
  courseInfo: {
    fontSize: 14,
    marginBottom: 12,
  },
  attendanceContainer: {
    marginBottom: 12,
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statNumber: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  statLabel: {
    fontSize: 12,
  },
  attendanceButton: {
    flex: 1,
    marginHorizontal: 4,
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: 'bold',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    textAlign: 'center',
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
  },
});

export default AttendanceScreen;
